import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LockIcon from "@mui/icons-material/Lock";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import PointOfSaleOutlinedIcon from "@mui/icons-material/PointOfSaleOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import moment from "moment";
import "moment/locale/pt-br";
import { Shell } from "../../common/shell/shell.page";
import { PageHeader } from "../../common/components/page-header";
import { LoadingOverlay } from "../../common/components/loading-overlay";
import application from "../../core/application";
import { useModuleAccess } from "../../settings/module-access";
import { ModuleLockedView } from "../../settings/components/module-locked-view";
import { Permission } from "../../users/user.entity";
import { cashSessionRepository } from "../cash-session.repository";
import type { CashSession } from "../cash-session.entity";
import { sellRepository } from "../../sell/sell.repository";
import type { Sell } from "../../sell/sell.entity";
import { buildCashRegisterSummary } from "../cash-register-summary";
import { CashRegisterReportView } from "../components/cash-register-report-view";
import { CashRegisterReportHero } from "../components/cash-register-report-hero";
import { cashMovementRepository } from "../cash-movement.repository";
import type { CashMovement } from "../cash-movement.entity";
import { CashMovementsLogView } from "../components/cash-movements-log-view";

const money = (n: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n);

export function CashRegisterSessionDetailPage() {
  application.useHasPermission(Permission.CASH_REGISTER_MANAGEMENT);
  const theme = useTheme();
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();

  const moduleAccess = useModuleAccess();
  const featureOn = !moduleAccess.loading && moduleAccess.isEnabled("cash_register");

  const [session, setSession] = useState<CashSession | null | undefined>(undefined);
  const [sells, setSells] = useState<Sell[]>([]);
  const [movements, setMovements] = useState<CashMovement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!featureOn || !sessionId) {
      setSession(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const s = await cashSessionRepository.findByIdOrNull(sessionId);
        if (cancelled) return;
        setSession(s);
        if (!s) {
          setSells([]);
          setMovements([]);
          return;
        }
        const [list, movs] = await Promise.all([
          sellRepository.getSellsByCashSessionId(s.id),
          cashMovementRepository.listBySession(s.id),
        ]);
        if (!cancelled) {
          setSells(list);
          setMovements(movs);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Erro ao carregar sessão.");
          setSession(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [featureOn, sessionId]);

  const summary = useMemo(() => {
    if (!session) return null;
    return buildCashRegisterSummary(session, sells, movements);
  }, [session, sells, movements]);

  if (moduleAccess.loading) {
    return (
      <Shell title="Detalhe do caixa">
        <LoadingOverlay message="Carregando módulo…" subtle />
      </Shell>
    );
  }

  if (!featureOn) {
    return (
      <Shell title="Detalhe do caixa">
        <PageHeader title="Detalhe do caixa" description="Conferência da sessão." />
        <ModuleLockedView
          moduleName="Caixa"
          groupId="cash_register"
          details="Ative o módulo Caixa para acessar o detalhe das sessões e conferência de turno."
          features={["Detalhe de cada sessão de caixa", "Conferência de turno e sangrias", "Resumo financeiro por sessão"]}
        />
      </Shell>
    );
  }

  if (!sessionId) {
    return (
      <Shell title="Detalhe do caixa">
        <Alert severity="error">Sessão inválida.</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/caixa/relatorio")}>
          Voltar aos relatórios
        </Button>
      </Shell>
    );
  }

  const isClosed = session?.getStatus() === "closed";
  const openedAtLabel = session
    ? moment(session.getOpenedAt()).format("DD/MM/YYYY [às] HH:mm")
    : null;
  const closedAtLabel = session?.getClosedAt()
    ? moment(session.getClosedAt()).format("DD/MM/YYYY [às] HH:mm")
    : null;

  return (
    <Shell title="Detalhe do caixa">
      {loading ? <LoadingOverlay message="Carregando sessão…" subtle /> : null}

      {/* ── Breadcrumbs + Voltar ── */}
      <Stack spacing={1.5} sx={{ mb: 3 }}>
        <Breadcrumbs separator="›" sx={{ "& .MuiTypography-root": { fontSize: 14 } }}>
          <Typography
            component={Link}
            to="/caixa/relatorio"
            color="primary"
            sx={{ textDecoration: "none", fontWeight: 600, "&:hover": { textDecoration: "underline" } }}
          >
            Relatórios de caixa
          </Typography>
          <Typography color="text.primary" fontWeight={600}>
            {session ? moment(session.getDayKey(), "YYYY-MM-DD").format("LL") : "…"}
          </Typography>
        </Breadcrumbs>

        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/caixa/relatorio")}
          sx={{ alignSelf: "flex-start", fontWeight: 700 }}
        >
          Voltar à lista
        </Button>
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      ) : null}

      {!loading && session === null ? (
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: alpha(theme.palette.error.main, 0.04),
          }}
        >
          <CardContent>
            <Typography fontWeight={700} gutterBottom>
              Sessão não encontrada
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Este registro pode ter sido removido ou o link está incorreto.
            </Typography>
            <Button variant="contained" component={Link} to="/caixa/relatorio">
              Ir para relatórios
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {session && summary ? (
        <Stack spacing={3}>
          {/* ── Hero ── */}
          <CashRegisterReportHero
            overline="Relatório de caixa"
            title={moment(session.getDayKey(), "YYYY-MM-DD").format("dddd, D [de] MMMM [de] YYYY")}
            chips={
              <>
                {openedAtLabel && (
                  <Chip
                    icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 16 }} />}
                    label={`Aberto em ${openedAtLabel}`}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    }}
                  />
                )}
                {closedAtLabel && (
                  <Chip
                    label={`Fechado em ${closedAtLabel}`}
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                  />
                )}
                <Chip
                  label={isClosed ? "Encerrado" : "Em aberto"}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    bgcolor: isClosed
                      ? alpha(theme.palette.success.main, 0.12)
                      : alpha(theme.palette.warning.main, 0.12),
                    border: `1px solid ${isClosed
                      ? alpha(theme.palette.success.main, 0.28)
                      : alpha(theme.palette.warning.main, 0.28)}`,
                    color: isClosed ? "success.dark" : "warning.dark",
                  }}
                />
              </>
            }
          />

          {/* ── Stat cards ── */}
          <Grid container spacing={2}>
            {/* Linha 1 */}
            <Grid item xs={12} sm={4}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.background.paper, 1)} 60%)`,
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Abertura do caixa
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, letterSpacing: "-0.02em" }}>
                        {money(summary.openingCash)}
                      </Typography>
                    </Box>
                    <LockOpenIcon sx={{ color: "primary.main", opacity: 0.85 }} />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  background: `linear-gradient(145deg, ${alpha(theme.palette.success.main, 0.09)} 0%, ${alpha(theme.palette.background.paper, 1)} 60%)`,
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Total vendido
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, letterSpacing: "-0.02em" }}>
                        {money(summary.totalSales)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                        {summary.countCompletedSales} vendas concluídas
                      </Typography>
                    </Box>
                    <TrendingUpIcon sx={{ color: "success.main", opacity: 0.85 }} />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                  background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.background.paper, 1)} 60%)`,
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {isClosed ? "Valor contado no fechamento" : "Esperado em caixa"}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, letterSpacing: "-0.02em", color: "primary.dark" }}>
                        {isClosed && summary.closingCash !== null
                          ? money(summary.closingCash)
                          : money(summary.expectedCashApprox)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                        Abertura + entradas − retiradas + dinheiro
                      </Typography>
                    </Box>
                    {isClosed
                      ? <LockIcon sx={{ color: "primary.main", opacity: 0.85 }} />
                      : <AccountBalanceWalletOutlinedIcon sx={{ color: "primary.main", opacity: 0.85 }} />
                    }
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Linha 2 */}
            <Grid item xs={12} sm={4}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  background: `linear-gradient(145deg, ${alpha(theme.palette.success.main, 0.06)} 0%, ${alpha(theme.palette.background.paper, 1)} 60%)`,
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Vendas em dinheiro
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, letterSpacing: "-0.02em" }}>
                        {money(summary.totalCashSales)}
                      </Typography>
                    </Box>
                    <PointOfSaleOutlinedIcon sx={{ color: "success.main", opacity: 0.75 }} />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  background: `linear-gradient(145deg, ${alpha(theme.palette.info.main, 0.08)} 0%, ${alpha(theme.palette.background.paper, 1)} 60%)`,
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Entradas manuais
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, letterSpacing: "-0.02em" }}>
                        {money(summary.totalEntries)}
                      </Typography>
                    </Box>
                    <ArrowUpwardIcon sx={{ color: "info.main", opacity: 0.85 }} />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  background: `linear-gradient(145deg, ${alpha(theme.palette.error.main, 0.07)} 0%, ${alpha(theme.palette.background.paper, 1)} 60%)`,
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Retiradas
                      </Typography>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 800,
                          mt: 0.5,
                          letterSpacing: "-0.02em",
                          color: summary.totalWithdrawals > 0 ? "error.main" : "text.primary",
                        }}
                      >
                        {money(summary.totalWithdrawals)}
                      </Typography>
                    </Box>
                    <ArrowDownwardIcon sx={{ color: "error.main", opacity: 0.75 }} />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* ── Relatório de vendas ── */}
          <Card elevation={0}>
            <Box sx={{ px: 2.5, pt: 2.5, pb: 2, borderBottom: 1, borderColor: "divider" }}>
              <Typography variant="subtitle1" fontWeight={700}>
                Relatório de vendas
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Resumo por forma de pagamento e conferência do saldo em dinheiro.
              </Typography>
            </Box>
            <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
              <CashRegisterReportView
                summary={summary}
                title=""
                showHealth={isClosed}
              />
            </CardContent>
          </Card>

          {/* ── Log de movimentações ── */}
          <Card elevation={0}>
            <Box sx={{ px: 2.5, pt: 2.5, pb: 2, borderBottom: 1, borderColor: "divider" }}>
              <Typography variant="subtitle1" fontWeight={700}>
                Log de movimentações
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Histórico de entradas, retiradas e vendas em dinheiro desta sessão.
              </Typography>
            </Box>
            <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
              <CashMovementsLogView
                openingCash={session.getOpeningCash()}
                movements={movements}
                sells={sells}
              />
            </CardContent>
          </Card>

          <Box sx={{ pb: 2 }} />
        </Stack>
      ) : null}
    </Shell>
  );
}
